import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, AlertCircle, ArrowRight, Sparkles, CheckCircle2, Hash, Phone, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLogin } from '../hooks/useLogin';
import { usePhoneLogin } from '../hooks/usePhoneLogin';
import api from '../api/axios';

const schema = z.object({
  email:       z.string().min(1, 'Email or username is required'),
  password:    z.string().min(1, 'Password is required'),
  societyCode: z.string().optional(),
}).superRefine((data, ctx) => {
  const looksLikeEmail = data.email.includes('@');
  if (!looksLikeEmail && !data.societyCode?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['societyCode'],
      message: 'Society code is required when signing in with a username',
    });
  }
});

type LoginForm = z.infer<typeof schema>;

const inputBase: React.CSSProperties = {
  width: '100%',
  background: 'var(--nx-surface-2)',
  borderRadius: 10,
  fontSize: 14,
  color: 'var(--nx-text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 150ms',
};

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { submit, isLoading, error } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();

  const state = location.state as { registered?: boolean; email?: string; username?: string; societyCode?: string; requested?: boolean; passwordReset?: boolean } | null;
  const registered    = state?.registered  ?? false;
  const registeredEmail = state?.email ?? state?.username ?? '';
  const registeredSocietyCode = state?.societyCode ?? '';
  const requested     = state?.requested   ?? false;
  const passwordReset = state?.passwordReset ?? false;

  const [emailValue, setEmailValue] = useState(registeredEmail);

  const isUsername = emailValue.length > 0 && !emailValue.includes('@');

  const [mode, setMode] = useState<'password' | 'phone'>('password');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const phoneLogin = usePhoneLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: registeredEmail, societyCode: registeredSocietyCode },
  });

  const emailReg = register('email');

  // Cloud Run scales the backend to zero when idle, so kick off its cold
  // start as soon as this page loads — by the time someone finishes typing
  // their credentials, the server has usually already woken up.
  useEffect(() => {
    api.get('/api/v1/status').catch(() => {});
  }, []);

  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const borderColor = (hasError: boolean) =>
    hasError ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nx-navy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(26,108,246,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />
      {/* Radial glow */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '500px',
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(26,108,246,0.14) 0%, rgba(109,40,217,0.07) 40%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Card */}
      <div
        className="nx-anim-fade-up"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,108,246,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ height: 3, background: 'linear-gradient(90deg, #1A6CF6, #6D28D9)' }} />

        <div style={{ padding: '36px 32px' }}>
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff' }}>
                Nx
              </span>
            </div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: 'var(--nx-text-primary)', letterSpacing: '-0.02em' }}>
                NEXORA
              </div>
              <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', letterSpacing: '0.08em' }}>
                Pentagon Analytics
              </div>
            </div>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 6 }}>
              Sign in to Nexora
            </h1>
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
              Enter your credentials to access your dashboard.
            </p>
            <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', opacity: 0.7, marginTop: 6 }}>
              Nexora Nest · Society Management Platform
            </p>
          </div>

          {/* Banners */}
          {requested && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(26,108,246,0.08)', border: '1px solid rgba(26,108,246,0.25)', borderRadius: 10, marginBottom: 20 }}>
              <CheckCircle2 size={15} style={{ color: '#1A6CF6', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#1A6CF6' }}>Request received! We'll send your invite within 1–2 business days.</span>
            </div>
          )}
          {registered && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10, marginBottom: 20 }}>
              <CheckCircle2 size={15} style={{ color: '#10B981', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#10B981' }}>Account created! Sign in to continue.</span>
            </div>
          )}
          {passwordReset && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10, marginBottom: 20 }}>
              <CheckCircle2 size={15} style={{ color: '#10B981', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#10B981' }}>Password reset successfully. Sign in with your new password.</span>
            </div>
          )}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, marginBottom: 20 }}>
              <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#EF4444' }}>{error}</span>
            </div>
          )}
          {phoneLogin.error && mode === 'phone' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, marginBottom: 20 }}>
              <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#EF4444' }}>{phoneLogin.error}</span>
            </div>
          )}

          {/* Mode toggle */}
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'password' ? 'phone' : 'password');
              phoneLogin.reset();
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-blue)', fontSize: 12, fontWeight: 500, marginBottom: 16, padding: 0 }}
          >
            {mode === 'password' ? <Phone size={13} /> : <Mail size={13} />}
            {mode === 'password' ? 'Sign in with phone number instead' : 'Sign in with email or username instead'}
          </button>

          {mode === 'phone' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="phone" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                  MOBILE NUMBER
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ ...inputBase, padding: '11px 14px', border: '1px solid var(--nx-border)', width: 56, textAlign: 'center', flexShrink: 0 }}>+91</span>
                  <input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    autoFocus
                    disabled={phoneLogin.otpSent}
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    style={{ ...inputBase, padding: '11px 14px', border: '1px solid var(--nx-border)', opacity: phoneLogin.otpSent ? 0.6 : 1 }}
                  />
                </div>
                <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>
                  For residents only — we'll send a one-time code via WhatsApp.
                </p>
              </div>

              {phoneLogin.otpSent && (
                <div>
                  <label htmlFor="otp" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                    ENTER CODE
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={{ ...inputBase, padding: '11px 14px', border: '1px solid var(--nx-border)', fontSize: 20, letterSpacing: '0.3em', textAlign: 'center' }}
                  />
                  <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>
                    Sent to +91 {phone}. Expires in 10 minutes.
                  </p>
                </div>
              )}

              <button
                type="button"
                disabled={phoneLogin.otpSent ? otp.length !== 6 || phoneLogin.verifying : phone.length !== 10 || phoneLogin.requesting}
                onClick={() => phoneLogin.otpSent ? phoneLogin.verifyOtp(`+91${phone}`, otp) : phoneLogin.requestOtp(`+91${phone}`)}
                className="nx-btn nx-btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14, fontWeight: 600, marginTop: 4 }}
              >
                {phoneLogin.otpSent
                  ? (phoneLogin.verifying ? 'Verifying...' : 'Verify & Sign In')
                  : (phoneLogin.requesting ? 'Sending...' : 'Send OTP')}
              </button>
              {phoneLogin.otpSent && (
                <button
                  type="button"
                  onClick={phoneLogin.reset}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', fontSize: 12, textAlign: 'center' }}
                >
                  Change number
                </button>
              )}
            </div>
          ) : (

          <form onSubmit={handleSubmit(submit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} noValidate>

            {/* Email / Username */}
            <div>
              <label htmlFor="email" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                EMAIL OR USERNAME
              </label>
              <input
                id="email"
                type="text"
                autoComplete="username"
                autoFocus
                placeholder="admin@nexora.io or swetha"
                {...emailReg}
                onChange={(e) => {
                  emailReg.onChange(e);
                  setEmailValue(e.target.value);
                }}
                style={{ ...inputBase, padding: '11px 14px', border: `1px solid ${borderColor(!!errors.email)}` }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                onBlur={(e) => (e.currentTarget.style.borderColor = borderColor(!!errors.email))}
              />
              {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.email.message}</p>}
            </div>

            {/* Society code — required for residents, not needed for society
                admin/staff email logins. Always shown so residents whose username
                happens to be their email still have a place to disambiguate. */}
            <div style={{ padding: '14px', background: 'rgba(26,108,246,0.04)', border: '1px solid rgba(26,108,246,0.14)', borderRadius: 10 }}>
              <label htmlFor="societyCode" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                SOCIETY CODE {!isUsername && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>(residents only)</span>}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="societyCode"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. NEX0001"
                  {...register('societyCode')}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    register('societyCode').onChange(e);
                  }}
                  style={{ ...inputBase, padding: '11px 42px 11px 14px', border: `1px solid ${borderColor(!!errors.societyCode)}`, textTransform: 'uppercase' }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = borderColor(!!errors.societyCode))}
                />
                <Hash size={15} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
              </div>
              {errors.societyCode
                ? <p style={{ fontSize: 11, color: '#EF4444', marginTop: 6 }}>{errors.societyCode.message}</p>
                : <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>
                    {isUsername
                      ? 'Find your society code on the notice board or ask your secretary.'
                      : 'Leave blank if you’re signing in as a society admin or staff member.'}
                  </p>
              }
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label htmlFor="password" style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', letterSpacing: '0.04em' }}>
                  PASSWORD
                </label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
                  Forgot password?
                </Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  {...register('password')}
                  style={{ ...inputBase, padding: '11px 42px 11px 14px', border: `1px solid ${borderColor(!!errors.password)}` }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = borderColor(!!errors.password))}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.password.message}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="nx-btn nx-btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14, fontWeight: 600, marginTop: 4, opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
            >
              {isLoading ? (
                <>
                  <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                  Signing in...
                </>
              ) : (
                <>Sign in <ArrowRight size={15} /></>
              )}
            </button>
          </form>
          )}

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--nx-border)' }} />
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--nx-border)' }} />
          </div>

          {/* Footer badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: 'linear-gradient(135deg, rgba(26,108,246,0.06), rgba(109,40,217,0.04))', border: '1px solid rgba(26,108,246,0.12)', borderRadius: 10 }}>
            <Sparkles size={13} style={{ color: 'var(--nx-blue)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--nx-text-secondary)' }}>
              AI-powered community management · Secured by Pentagon Analytics
            </span>
          </div>

          {/* Request access */}
          <p style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--nx-text-muted)' }}>
            Need access?{' '}
            <Link to="/request-access" style={{ color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
              Request it here
            </Link>
          </p>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, marginTop: 24, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        © {new Date().getFullYear()} Pentagon Analytics. All rights reserved.
      </div>
    </div>
  );
};

export default LoginPage;
